import * as React from 'react'
import { ArrowLeft, MessageSquare as AskIcon } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  useGetProblemOccurrenceQuery,
  useListIncidentDraftsQuery,
  useWorkScope,
  workScopeKey,
  type IncidentDraft,
  type OccurrenceInvestigation,
  type ProblemDebugItem,
  type ProblemOccurrence,
  type WorkScope
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { StatusPill } from '../../components/primitives/StatusPill'
import { ComponentChip } from '../../components/primitives/ComponentChip'
import { OccurrenceTasks, WorkError } from '../../components/problem-work/OccurrenceTasks'
import { OccurrenceRuns } from '../../components/problem-work/OccurrenceRuns'
import { OccurrenceSuppression } from '../../components/problem-work/OccurrenceSuppression'
import { DRAFT_POLL_MS } from '../../components/problem-work/DraftProblems'
import {
  OccurrenceStatusButton,
  useOccurrenceStatusAction
} from '../../components/problem-work/OccurrenceStatusAction'
import {
  DraftTasks,
  EvidenceColumns,
  EvidencePanel,
  InvestigationProgress,
  StartHere,
  useInvestigationStages
} from '../../components/problem-work/ProblemEvidence'
import {
  investigationStages,
  type InvestigationStages
} from '../../components/problem-work/investigation-model'
import {
  occurrenceContext,
  occurrenceLocation,
  workMutationError
} from '../../components/problem-work/work-model'
import {
  PROBLEM_RUN_QUERY_PARAM,
  PROBLEMS_SCOPE_QUERY_PARAM,
  PROBLEMS_PATH
} from '../../lib/component-links'
import { formatCompactLocalTime } from '../../lib/time'
import { useChatAppContext } from '../../chat-app-context'
import { useChatHandoff } from '../../lib/use-chat-handoff'

export default function ProblemPage() {
  const scope = useWorkScope()
  const { occurrenceId = '' } = useParams()
  return <ScopedProblemPage key={`${workScopeKey(scope)}:${occurrenceId}`} id={occurrenceId} />
}

function notFound(error: unknown) {
  return Boolean(error && typeof error === 'object' && 'status' in error && error.status === 404)
}

function ScopedProblemPage({ id }: { id: string }) {
  const scope = useWorkScope()
  const [params] = useSearchParams()
  const runId = params.get(PROBLEM_RUN_QUERY_PARAM) ?? undefined
  // A draft is not published yet, so the occurrence read 404s; poll until it publishes.
  const published = useGetProblemOccurrenceQuery(
    { ...scope, id },
    { refetchOnMountOrArgChange: true, pollingInterval: runId ? DRAFT_POLL_MS : 0 }
  )
  const unpublished = Boolean(runId) || notFound(published.error)
  const drafts = useListIncidentDraftsQuery(
    { ...scope, ...(runId ? { runId } : {}) },
    { skip: !unpublished || Boolean(published.currentData), pollingInterval: DRAFT_POLL_MS }
  )
  const draft = drafts.currentData?.find((row) => row.occurrence.id === id)
  const occurrence = published.currentData
  useChatAppContext(
    occurrence || draft
      ? [
          { key: 'Problem occurrence id', value: id },
          { key: 'Problem concept id', value: (occurrence ?? draft!.occurrence).problem_id },
          { key: 'Problem', value: (occurrence ?? draft!.occurrence).problem_name }
        ]
      : undefined
  )
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-5">
      <Link
        to={
          occurrence?.status === 'closed'
            ? `${PROBLEMS_PATH}?${PROBLEMS_SCOPE_QUERY_PARAM}=closed`
            : PROBLEMS_PATH
        }
        className="inline-flex w-fit items-center gap-1 text-13 font-medium text-brand no-underline hover:underline"
      >
        <ArrowLeft size={14} aria-hidden />
        Problems
      </Link>
      {occurrence ? (
        <PublishedProblem scope={scope} occurrence={occurrence} />
      ) : draft ? (
        <DraftProblem draft={draft} />
      ) : (published.isError && !notFound(published.error)) || drafts.isError ? (
        <WorkError
          message="Couldn't load this problem."
          retry={() => {
            if (published.isError) void published.refetch()
            if (drafts.isError) void drafts.refetch()
          }}
        />
      ) : unpublished && drafts.currentData ? (
        <p role="status" className="m-0 text-13 text-ink-3">
          This problem is no longer available.
        </p>
      ) : (
        <p role="status" className="m-0 py-8 text-13 text-ink-3">
          Loading problem…
        </p>
      )}
    </div>
  )
}

function PublishedProblem({
  scope,
  occurrence
}: {
  scope: WorkScope
  occurrence: ProblemOccurrence
}) {
  const investigation = useInvestigationStages(scope, occurrence)
  const items = investigation.items.isError ? [] : (investigation.items.currentData ?? [])
  const status = useOccurrenceStatusAction(scope, occurrence)
  const { closed, error: actError } = status
  const { askInChat } = useChatHandoff()
  return (
    <ProblemLayout
      occurrence={occurrence}
      stages={investigation.stages}
      items={items}
      status={
        closed ? (
          <StatusPill status="idle">Closed</StatusPill>
        ) : (
          <StatusPill status="down">Open</StatusPill>
        )
      }
      actions={
        <>
          <Button
            size="sm"
            variant="outline"
            className="text-brand"
            onClick={() =>
              askInChat({ title: occurrence.problem_name, context: occurrenceContext(occurrence) })
            }
          >
            <AskIcon size={14} />
            Ask in chat
          </Button>
          <OccurrenceStatusButton action={status} />
        </>
      }
      error={
        investigation.items.isError ? (
          <WorkError
            message="Couldn't load the evidence."
            retry={() => void investigation.items.refetch()}
          />
        ) : actError ? (
          <p role="alert" className="m-0 text-13 text-st-down">
            {workMutationError(actError)}
          </p>
        ) : null
      }
      tasks={
        investigation.stages.actions === 'waiting' ? (
          <p className="m-0 px-3 py-2.5 text-13 text-ink-3">
            Tasks are written after the evidence is gathered, and cite it.
          </p>
        ) : (
          <OccurrenceTasks scope={scope} occurrence={occurrence} details />
        )
      }
      footer={
        <>
          <OccurrenceRuns scope={scope} id={occurrence.id} />
          <div className="flex flex-wrap items-center justify-end gap-3 empty:hidden">
            <OccurrenceSuppression scope={scope} id={occurrence.id} />
          </div>
        </>
      }
    />
  )
}

function DraftProblem({ draft }: { draft: IncidentDraft }) {
  const stages = investigationStages({
    runs: [{ run_id: draft.run_id, status: 'running' } as OccurrenceInvestigation],
    likelyCause: draft.occurrence.likely_cause,
    debugItems: draft.debug_items.length,
    tasks: draft.tasks.length
  })
  return (
    <ProblemLayout
      occurrence={draft.occurrence}
      stages={stages}
      items={draft.debug_items}
      status={
        <StatusPill status="info" withDot={false}>
          Being investigated · published when the run finishes
        </StatusPill>
      }
      tasks={<DraftTasks tasks={draft.tasks} />}
    />
  )
}

/** Design A: the issue and likely cause, three places to start, then all evidence and the tasks. */
function ProblemLayout({
  occurrence,
  stages,
  items,
  status,
  actions,
  error,
  tasks,
  footer
}: {
  occurrence: ProblemOccurrence
  stages: InvestigationStages
  items: readonly ProblemDebugItem[]
  status: React.ReactNode
  actions?: React.ReactNode
  error?: React.ReactNode
  tasks: React.ReactNode
  footer?: React.ReactNode
}) {
  const [open, setOpen] = React.useState<ProblemDebugItem | null>(null)
  const location = occurrenceLocation(occurrence)
  const affected = occurrence.affected_entities.filter((item) => item.type !== 'Line')
  return (
    <>
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-12 text-ink-3">
          {status}
          <InvestigationProgress stages={stages} />
          {location && <span>{location}</span>}
          <span>
            Opened{' '}
            <time dateTime={occurrence.opened_at}>
              {formatCompactLocalTime(occurrence.opened_at)}
            </time>
            {occurrence.closed_at && (
              <>
                {' · Closed '}
                <time dateTime={occurrence.closed_at}>
                  {formatCompactLocalTime(occurrence.closed_at)}
                </time>
              </>
            )}
          </span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="m-0 min-w-0 break-words text-2xl font-semibold tracking-tight text-ink">
            {occurrence.problem_name || 'New problem'}
          </h1>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
        {occurrence.description && (
          <p className="m-0 max-w-3xl whitespace-pre-wrap break-words text-14 leading-relaxed text-ink-2">
            {occurrence.description}
          </p>
        )}
        {occurrence.likely_cause ? (
          <div className="max-w-3xl rounded-r-md border-l-[3px] border-st-warn bg-st-warn-bg/60 px-3 py-2">
            <p className="m-0 text-11 font-semibold uppercase tracking-wide text-st-warn">
              Likely cause
            </p>
            <p className="m-0 text-14 text-ink">{occurrence.likely_cause}</p>
          </div>
        ) : (
          stages.investigating && (
            <p className="m-0 text-13 text-ink-3">Finding the likely cause…</p>
          )
        )}
        {affected.length > 0 && (
          <div
            aria-label="Affected equipment"
            className="flex flex-wrap items-center gap-1.5 text-12 text-ink-3"
          >
            Affects
            {affected.map((item) => (
              <ComponentChip
                key={item.id}
                component={{
                  id: item.id,
                  name: item.name,
                  scope: item.type === 'Tag' ? 'tag' : 'equipment'
                }}
                hideScope
                className="max-w-full rounded border-line/70 bg-bg-sunk/50 px-1.5 py-px text-12 font-normal text-ink-2 [&>span]:truncate"
              />
            ))}
          </div>
        )}
      </header>
      {error}
      <StartHere items={items} onOpen={setOpen} />
      <EvidenceColumns
        items={items}
        collecting={stages.debugInfo === 'active' || stages.debugInfo === 'waiting'}
        onOpen={setOpen}
      />
      <section aria-label="Tasks" className="flex flex-col gap-2">
        <h2 className="m-0 text-12 font-semibold uppercase tracking-wide text-ink-2">Tasks</h2>
        <div className="overflow-hidden rounded-lg border border-line bg-card">{tasks}</div>
      </section>
      {footer}
      <EvidencePanel item={open} occurrence={occurrence} onClose={() => setOpen(null)} />
    </>
  )
}
